import { MouseEvent, useEffect, useRef, useState } from 'react';

import { useIsClient } from 'usehooks-ts';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';

import useScript from '@store:web/utils/hooks/useScript';

const addSoquestQuestReadableClasses = (rootElement: HTMLElement) => {
  rootElement.classList.add('soquest-quest-widget__main');

  rootElement.querySelector('.css-1xsy67q')?.classList.add('soquest-quest-widget__container');
  rootElement.querySelector('.css-1lvewyp')?.classList.add('soquest-quest-widget__body');
  rootElement.querySelector('.css-18uxkby')?.classList.add('soquest-quest-widget__header');
  rootElement
    .querySelector('.css-1lvewyp > div:first-child')
    ?.classList.add('soquest-quest-widget__details');
  rootElement
    .querySelector('.css-ftssms')
    ?.classList.add('soquest-quest-widget__content-container');
  rootElement
    .querySelector('.css-ftssms > div:nth-child(1)')
    ?.classList.add('soquest-quest-widget__banner');
  rootElement.querySelector('.css-13cll9k')?.classList.add('soquest-quest-widget__banner-switch');
  rootElement.querySelector('.css-enkjhg')?.classList.add('soquest-quest-widget__banner-image');
  rootElement
    .querySelector('.css-ftssms > div:nth-child(2)')
    ?.classList.add('soquest-quest-widget__tabs');
  rootElement.querySelector('.css-u4p24i')?.classList.add('soquest-quest-widget__tabs-button-list');
  rootElement.querySelector('.css-1jhsce2')?.classList.add('soquest-quest-widget__tabs-tab');
  rootElement.querySelector('.css-11j9yrv')?.classList.add('soquest-quest-widget__tabs-tab-task');
  rootElement
    .querySelector('.css-1dwzqy2')
    ?.classList.add('soquest-quest-widget__tabs-tab-entities');
  rootElement
    .querySelector('.css-15w5l87')
    ?.classList.add('soquest-quest-widget__tabs-tab-rewards');
  rootElement
    .querySelector('.css-ftssms > div:nth-child(3)')
    ?.classList.add('soquest-quest-widget__stats');
};

const addSoquestLeaderboardReadableClasses = (rootElement: HTMLElement) => {
  rootElement
    .querySelector('.SoQuest-Widget-Rank')
    ?.classList.add('soquest-leaderboard-widget__main');
  rootElement.querySelector('.css-1mpbcu6')?.classList.add('soquest-leaderboard-widget__table');
  rootElement
    .querySelector('.css-cxxsiy')
    ?.classList.add('soquest-leaderboard-widget__table-header');
  rootElement
    .querySelector('.css-1yw7e41')
    ?.classList.add('soquest-leaderboard-widget__table-body');
  rootElement
    .querySelector('.css-b04h20')
    ?.classList.add('soquest-leaderboard-widget__table-footer');
};

function SoQuestWidgetQuest({ config }: { config: any }) {
  const { isAuth } = useAuth();
  const authModal = useAuthModal();
  const { scode, ccode, key, leaderboardId, description } = config || {};
  const hasEssentialConfigData = !!scode && !!ccode && !!key;
  const widgetElRef = useRef(null);
  const [isLeaderboardInjected, setIsLeaderboardInjected] = useState(false);
  const isClient = useIsClient();

  useScript({
    src: 'https://qaas.soquest.xyz/quest/static/js/quest.js',
    defer: true,
    enabled: isClient && hasEssentialConfigData,
  });

  useScript({
    src: 'https://qaas.soquest.xyz/ranking/static/js/rank.js',
    enabled: leaderboardId && isLeaderboardInjected,
    defer: true,
  });

  const handleClicks = (event: MouseEvent<HTMLDivElement>) => {
    if (!isAuth) {
      event.stopPropagation();
      authModal.open();
    }
  };

  useEffect(() => {
    if (!hasEssentialConfigData) {
      return;
    }

    const widgetEl: any = widgetElRef.current;

    const onceChildrenEmbedded = (element: HTMLElement, cb: (arg: any) => void) => {
      const observer = new MutationObserver(([mutation]) => {
        if (mutation.type !== 'childList') {
          return;
        }

        cb(mutation);

        observer.disconnect();
      });

      observer.observe(element, { childList: true });

      return observer;
    };

    let leaderboardEmbeddingObserver: any;
    const questEmbeddingObserver = onceChildrenEmbedded(widgetEl, () => {
      if (!widgetEl) {
        return;
      }

      /**
       * @type {HTMLDivElement}
       */
      const widgetMainEl = widgetEl?.querySelector('.css-6c0u11');

      if (!widgetMainEl) {
        return;
      }

      addSoquestQuestReadableClasses(widgetMainEl);

      if (!leaderboardId) {
        return;
      }

      /**
       * @type {HTMLDivElement}
       */
      const widgetBodyEl = widgetMainEl.querySelector('.css-1lvewyp');

      if (!widgetBodyEl.querySelector('.SoQuest-Widget-Rank')) {
        const leaderboardEl = document.createElement('div');

        leaderboardEl.setAttribute('class', 'SoQuest-Widget-Rank soquest-leaderboard-widget');
        leaderboardEl.setAttribute('data-key', key);
        leaderboardEl.setAttribute('data-mode', 'light');
        leaderboardEl.setAttribute('data-scode', scode);
        leaderboardEl.setAttribute('data-ccode', ccode);
        leaderboardEl.setAttribute('data-id', leaderboardId);

        widgetBodyEl.appendChild(leaderboardEl);

        setIsLeaderboardInjected(true);

        leaderboardEmbeddingObserver = onceChildrenEmbedded(leaderboardEl, () => {
          if (!leaderboardEl) {
            return;
          }

          addSoquestLeaderboardReadableClasses(leaderboardEl);
        });
      }
    });

    return () => {
      questEmbeddingObserver.disconnect();

      if (leaderboardEmbeddingObserver) {
        leaderboardEmbeddingObserver.disconnect();
      }
    };
  }, [leaderboardId, description, ccode, key, scode, hasEssentialConfigData]);

  return (
    <div
      ref={widgetElRef}
      className="soquest-quest-widget SoQuest-Widget-Quest"
      data-mode="light"
      data-key={key}
      data-scode={scode}
      data-ccode={ccode}
      data-banner="1"
      data-reward="1"
      onClickCapture={handleClicks}
    />
  );
}

export default SoQuestWidgetQuest;
